import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Sidebar from './components/Sidebar.tsx';
import About from './components/About';
import ProjectsPage from './components/ProjectsPage';
import BlogsPage from './components/BlogsPage';
import BlogPost from './components/BlogPost';
import CommandPalette from './components/CommandPalette';
import TerminalIntro from './components/TerminalIntro';
import ChatWidget from './components/Chat/ChatWidget';
import type { ChatOpenRequest } from './components/Chat/ChatWidget';
import { useDynamicScale } from './hooks/useDynamicScale';
import { useLatch } from './hooks/useLatch';
import { prewarmApiConnection } from './services/apiPrewarm';

// Easter-egg effects, reachable only via the CommandPalette — never needed
// on first paint, so they're split out of the main chunk. Suspense fallback
// is null (not a spinner): each is a full-page takeover triggered by an
// explicit user action, so a visible loading state would be worse than the
// brief fetch delay.
const PortalRevealEffect = lazy(() => import('./components/PortalRevealEffect'));
const BlackHoleEffect = lazy(() => import('./components/BlackHoleEffect'));
const EnableGravityEffect = lazy(() => import('./components/EnableGravityEffect'));
const GravitationalLens = lazy(() => import('./components/GravitationalLens'));
const GoldenSnitchEffect = lazy(() => import('./components/GoldenSnitchEffect'));
const ChidoriRasenganEffect = lazy(() => import('./components/ChidoriRasenganEffect'));

function App() {
  const [showTerminal, setShowTerminal] = useState(true);

  // Enable dynamic scaling for all desktop displays
  useDynamicScale();

  // Fire-and-forget: warms the connection to the API origin so the first chat
  // message doesn't pay the TLS handshake. Nothing waits on it.
  useEffect(() => {
    prewarmApiConnection();
  }, []);

  useEffect(() => {
    if (showTerminal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showTerminal]);

  const [activeSection, setActiveSection] = useState('projects');
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [chatOpenRequest, setChatOpenRequest] = useState<ChatOpenRequest | null>(null);
  // Off by default — a Funhouse effect you opt into via the hidden "gravity
  // lens" palette command, not something every visitor gets unasked. Not
  // persisted across reloads, matching the theme toggle's current behavior.
  const [lensEnabled, setLensEnabled] = useState(false);

  const handleAskTars = useCallback((query: string) => {
    setChatOpenRequest({ query, nonce: Date.now() });
  }, []);

  // Portal Reveal easter egg. React only needs to know when the sequence
  // starts and ends — the slide-down/descend timing lives in CSS (see
  // .app-shell.portal-active and .portal-mirror in index.css: the shell
  // runs its own translateY, PortalRevealEffect runs an independent one).
  // The lock properties (position:fixed etc, pinning the shell to exactly
  // one viewport so a page taller than the viewport never slides a
  // nonsense slice into view) are baked into the same class as the
  // animation, so they engage and release in exact lockstep with it.
  const [portalActive, setPortalActive] = useState(false);
  // Sticky: mounts PortalRevealEffect (and fetches its chunk) on the first
  // trigger and never unmounts it again — see useLatch. Un-mounting on
  // portalActive->false would cut off the component's own exit animation.
  const portalUsed = useLatch(portalActive);
  const portalBusyRef = useRef(false);
  const portalScrollYRef = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const triggerPortal = useCallback(() => {
    if (portalBusyRef.current) return;
    portalBusyRef.current = true;

    // The locked frame is always exactly page-top, so we don't need to
    // preserve an arbitrary scroll offset inside a position:fixed box —
    // the effect is non-interactive and brief, so resetting to 0 and
    // restoring afterward is simpler and just as good.
    portalScrollYRef.current = window.scrollY;
    window.scrollTo({ top: 0 });
    setPortalActive(true);
  }, []);

  const handlePortalAnimationEnd = useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    // Only .app-shell's own animation should drive cleanup — the mirror
    // layer's animationend also bubbles up through the DOM, but reacting
    // to both would fire this twice per cycle.
    if (e.animationName !== 'portal-slide' && e.animationName !== 'portal-slide-reduced') return;
    setPortalActive(false);
    // Removing the class doesn't touch the DOM synchronously, so the shell
    // is still position:fixed (near-zero scroll range) right after this
    // call — scrolling here would just get clamped to 0. A single rAF
    // isn't reliably enough to guarantee the browser has actually painted
    // the unlock; nest two so the restore runs on the frame *after* the
    // one where the new layout is guaranteed to have committed.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: portalScrollYRef.current });
      });
    });
    portalBusyRef.current = false;
  }, []);

  // Black hole easter egg. Unlike the portal/lens effects, this one has a
  // two-step UX (arm via the palette, then click anywhere to actually
  // spawn), so BlackHoleEffect owns its own armed/running/finished state
  // internally — App only needs the coarse "is it active at all" boolean,
  // both to render the component and to keep it mutually exclusive with
  // the other full-page/cursor effects.
  const [blackHoleActive, setBlackHoleActive] = useState(false);
  // Sticky mount latch, same reasoning as portalUsed above.
  const blackHoleUsed = useLatch(blackHoleActive);

  // Golden Snitch easter egg. Unlike the black hole, this one has no
  // meaningful "arm" step — it just flies — so App only needs the coarse
  // active boolean plus the spawn point the palette captured at trigger
  // time. Reduced motion is handled inside GoldenSnitchEffect itself (a
  // slower, more catchable Snitch, not a disabled one) — deliberately not
  // the same "disable entirely" policy as the black hole/lens below.
  const [snitchActive, setSnitchActive] = useState(false);
  // Sticky mount latch, same reasoning as portalUsed above.
  const snitchUsed = useLatch(snitchActive);
  const [snitchOrigin, setSnitchOrigin] = useState<{ x: number; y: number } | null>(null);
  // Bumped (never reset to 0) to tell an already-flying Snitch to disarm —
  // selecting "Recall the Snitch" a second time while one is loose, same
  // toggle idiom as the gravity lens but needing a live signal rather than
  // a plain boolean flip, since GoldenSnitchEffect owns its own in-flight
  // phase and the disarm has to reach the current flight, not just flip a
  // prop the component reads once.
  const [snitchDisarmSignal, setSnitchDisarmSignal] = useState(0);

  // Enable Gravity easter egg. No arm step and no origin, unlike the black
  // hole/Snitch above — gravity applies to the whole page uniformly, so it
  // just runs the instant it's selected. Reduced motion gets the same
  // "disable entirely" policy as the black hole/lens: dozens of elements
  // falling and colliding at once has no meaningful reduced version.
  const [gravityActive, setGravityActive] = useState(false);
  // Sticky mount latch, same reasoning as portalUsed above.
  const gravityUsed = useLatch(gravityActive);

  // Chidori vs Rasengan easter egg. A ~5.5s non-interactive cutscene that
  // covers the page and hands it back untouched, so App only needs the
  // coarse active boolean — no arm step, no origin, nothing to clone.
  // Escape aborts it from inside the effect. Same "disable entirely" policy
  // as the black hole under reduced motion: a full-screen whiteout has no
  // honest reduced version.
  const [clashActive, setClashActive] = useState(false);
  // Sticky mount latch, same reasoning as portalUsed above.
  const clashUsed = useLatch(clashActive);

  // Cmd/Ctrl+K opens the palette. preventDefault stops the browser stealing it
  // for its search bar. Guarded against the three opaque full-page takeovers
  // (portal, black hole, gravity — all z-70/80, above the palette's z-60):
  // without this, Cmd+K during one of them opened the palette anyway, just
  // invisibly behind the effect, and — since it auto-focuses its input —
  // silently ate the *next* Escape press (CommandPalette's own Escape
  // handler calls stopPropagation and closes itself before the keypress
  // could reach the effect's own document-level Escape listener), reading
  // as "Escape doesn't close gravity" and "Cmd+K just re-triggers it" even
  // though neither literally happened. Declared here (after portalActive/
  // blackHoleActive/gravityActive exist) rather than up near isPaletteOpen —
  // the effect closure needs their current values, and referencing a const
  // in a dependency array before its declaration is a TDZ error.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (portalActive || blackHoleActive || gravityActive || clashActive) return;
        setIsPaletteOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [portalActive, blackHoleActive, gravityActive, clashActive]);

  const handleTriggerEffect = useCallback(
    (effectId: string, origin?: { x: number; y: number }) => {
      if (effectId === 'upside-down') {
        if (blackHoleActive || snitchActive || gravityActive || clashActive) return;
        triggerPortal();
      } else if (effectId === 'gravity-lens') {
        setLensEnabled((enabled) => !enabled);
      } else if (effectId === 'black-hole') {
        if (portalActive || snitchActive || gravityActive || clashActive) return;
        // A full-page physics takeover has no meaningful reduced version
        // the way a slide transition does — disable entirely rather than
        // run something half-working, same policy as the gravity lens.
        // The command stays listed in the palette either way; selecting it
        // just does nothing under reduced motion.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        setBlackHoleActive(true);
      } else if (effectId === 'golden-snitch') {
        if (snitchActive) {
          setSnitchDisarmSignal((n) => n + 1);
          return;
        }
        if (portalActive || blackHoleActive || gravityActive || clashActive) return;
        setSnitchOrigin(origin ?? null);
        setSnitchActive(true);
      } else if (effectId === 'enable-gravity') {
        if (portalActive || blackHoleActive || snitchActive || gravityActive || clashActive) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        setGravityActive(true);
      } else if (effectId === 'chidori-rasengan') {
        if (portalActive || blackHoleActive || snitchActive || gravityActive || clashActive) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        setClashActive(true);
      }
    },
    [triggerPortal, portalActive, blackHoleActive, snitchActive, gravityActive, clashActive]
  );

  return (
    <Router>
      <div
        className={`app-shell min-h-screen bg-dev-bg text-dev-text ${
          portalActive ? 'portal-active' : ''
        }`}
        onAnimationEnd={handlePortalAnimationEnd}
      >
        {showTerminal && <TerminalIntro onComplete={() => setShowTerminal(false)} />}
        <div
          ref={contentRef}
          className={`transition-opacity duration-500 ${showTerminal ? 'opacity-0' : 'opacity-100'}`}
        >
          {/* Desktop: Two-column fixed layout */}
          <div className="hidden lg:flex h-screen">
            {/* Left Column - Dynamically Scaled Sidebar */}
            <div className="flex-shrink-0 border-r border-dev-border" style={{ width: 'var(--sidebar-width)' }}>
              <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} onOpenPalette={() => setIsPaletteOpen(true)} />
            </div>

            {/* Right Column - Main Content */}
            <main className="flex-1 overflow-y-auto">
              <div className="max-w-[1200px] mx-auto" style={{ padding: 'var(--content-padding)' }}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <About
                        activeSection={activeSection}
                        onOpenPalette={() => setIsPaletteOpen(true)}
                        setActiveSection={setActiveSection}
                        onTriggerEffect={handleTriggerEffect}
                        lensEnabled={lensEnabled}
                        snitchActive={snitchActive}
                      />
                    }
                  />
                  <Route path="/projects" element={<ProjectsPage />} />
                  {/* Unlisted: reachable via the command palette or a direct link */}
                  <Route path="/blogs" element={<BlogsPage />} />
                  <Route path="/blogs/:id" element={<BlogPost />} />
                </Routes>
              </div>
            </main>
          </div>

          {/* Mobile: Simple single page with overlay menu */}
          <div className="lg:hidden min-h-screen">
            <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} onOpenPalette={() => setIsPaletteOpen(true)} />
            <main className="px-4 pb-32 pt-4">
              <Routes>
                <Route
                  path="/"
                  element={
                    <About
                      activeSection="all"
                      onOpenPalette={() => setIsPaletteOpen(true)}
                      setActiveSection={setActiveSection}
                      onTriggerEffect={handleTriggerEffect}
                      lensEnabled={lensEnabled}
                      snitchActive={snitchActive}
                    />
                  }
                />
                <Route path="/projects" element={<ProjectsPage />} />
                {/* Unlisted: reachable via the command palette or a direct link */}
                <Route path="/blogs" element={<BlogsPage />} />
                <Route path="/blogs/:id" element={<BlogPost />} />
              </Routes>
            </main>
          </div>
        </div>

        {!showTerminal && (
          <CommandPalette
            isOpen={isPaletteOpen}
            onClose={() => setIsPaletteOpen(false)}
            setActiveSection={setActiveSection}
            onAskTars={handleAskTars}
            onTriggerEffect={handleTriggerEffect}
            lensEnabled={lensEnabled}
            snitchActive={snitchActive}
          />
        )}

        {/* TARS Chatbot Widget */}
        {!showTerminal && <ChatWidget openRequest={chatOpenRequest} />}
      </div>

      {/* All six below are code-split out of the main chunk (see the lazy()
          calls above) — fallback null since none of these should ever show
          a loading state. Five are additionally gated on a sticky "*Used"
          latch (see useLatch) so the chunk isn't fetched until the effect
          is triggered for the first time — rendering a lazy component
          unconditionally still fetches its chunk immediately, prop value
          or not. Once mounted they stay mounted (never regated back to
          false) so each can run its own exit/cleanup and call onComplete.
          GravitationalLens doesn't need this: it's already gated behind
          !showTerminal, and disabled is just a prop on an already-cheap
          mounted component, not a fetch trigger. */}
      <Suspense fallback={null}>
        {/* Sibling, not a child of .app-shell — position:fixed descendants of a
            transformed ancestor get swept into its containing block, which
            would slide this along with the page instead of keeping it
            screen-locked. */}
        {portalUsed && <PortalRevealEffect active={portalActive} sourceRef={contentRef} />}

        {/* Same containing-block reasoning as PortalRevealEffect above — and
            the same clone-the-real-content approach, see BlackHoleEffect.tsx. */}
        {blackHoleUsed && (
          <BlackHoleEffect active={blackHoleActive} onComplete={() => setBlackHoleActive(false)} sourceRef={contentRef} />
        )}

        {/* Unlike the two effects above, this one doesn't need the
            containing-block workaround — it's a plain fixed-position overlay
            over live content, not nested inside anything transformed. Kept as
            a sibling anyway to group all the overlay effects together. */}
        {snitchUsed && (
          <GoldenSnitchEffect
            active={snitchActive}
            origin={snitchOrigin}
            disarmSignal={snitchDisarmSignal}
            onComplete={() => setSnitchActive(false)}
          />
        )}

        {/* Same containing-block reasoning as PortalRevealEffect above — and
            the same clone-the-real-content approach, see BlackHoleEffect.tsx. */}
        {gravityUsed && (
          <EnableGravityEffect active={gravityActive} onComplete={() => setGravityActive(false)} sourceRef={contentRef} />
        )}

        {/* The one effect that needs nothing from the page — it's an opaque
            WebGL scene that covers it rather than acting on it, so there's no
            sourceRef and nothing to clone. Kept a sibling with the rest for
            the same containing-block reason. */}
        {clashUsed && <ChidoriRasenganEffect active={clashActive} onComplete={() => setClashActive(false)} />}

        {/* Same containing-block reasoning as PortalRevealEffect above.
            Suppressed during the upside-down egg and the black hole egg
            since both are non-interactive full-page takeovers, during the
            Snitch (two cursor-reactive effects at once is noise), during
            Enable Gravity (another non-interactive full-page takeover), during
            the clash (same), and whenever toggled off via the "gravity lens"
            palette command. */}
        {!showTerminal && (
          <GravitationalLens
            disabled={
              portalActive ||
              blackHoleActive ||
              snitchActive ||
              gravityActive ||
              clashActive ||
              !lensEnabled
            }
          />
        )}
      </Suspense>
    </Router>
  );
}

export default App;